import axios from 'axios';
import { announceSessionExpired } from './sessionEvents';

// Dev: use relative /api so Vite proxy hits backend (same-origin → cookies work).
// Prod: use VITE_API_URL or /api (same host in typical deploy).
const API_BASE =
  import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_URL || '/api');

const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
  withCredentials: true,
});

// Backward-compatible named export used by some service modules.
export const apiClient = api;

// CSRF token (set by getCsrf(), attached to state-changing requests)
let csrfToken: string | null = null;

export function setInternalCsrfToken(token: string | null): void {
  csrfToken = token;
}

api.interceptors.request.use((config) => {
  if (csrfToken && ['post', 'put', 'patch', 'delete'].includes((config.method ?? '').toLowerCase())) {
    config.headers['X-CSRF-TOKEN'] = csrfToken;
  }
  return config;
});

// ── 401 Response Interceptor: auto-refresh + retry ───────────────────────
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

function processQueue(error: unknown) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve()));
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // Only handle 401 (not 403), skip if already retried or is the refresh call itself
    if (
      error.response?.status !== 401 ||
      original._retry ||
      original.url?.includes('/auth/refresh') ||
      original.url?.includes('/auth/login')
    ) {
      return Promise.reject(error);
    }

    // Queue concurrent requests while one refresh is in-flight
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: () => resolve(api(original)),
          reject,
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      await api.post('/auth/refresh');
      processQueue(null);
      return api(original);
    } catch (refreshError) {
      processQueue(refreshError);
      // Refresh failed — clear session hint so AuthContext knows, and tell the UI:
      // the user is asked to log in again in place (SessionExpiredModal), not left with silent 401s
      document.cookie = 'tender_session=; path=/; max-age=0';
      if ((refreshError as { response?: { status?: number } })?.response?.status === 401) announceSessionExpired();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

/**
 * POST that answers line by line (NDJSON) — axios cannot read a response while it arrives.
 * Same cookies, CSRF header and one refresh-and-retry on 401 as `api`.
 */
export async function postStream(path: string, body: unknown, onLine: (line: unknown) => void, signal?: AbortSignal): Promise<void> {
  const send = () => fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}) },
    body: JSON.stringify(body),
    signal,
  });
  let res = await send();
  if (res.status === 401) {
    await api.post('/auth/refresh');
    res = await send();
  }
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (line) onLine(JSON.parse(line));
    }
  }
  if (buf.trim()) onLine(JSON.parse(buf));
}

export default api;
